import RepeatState from '../../models/repeatState';
import Track from '../../models/track';
import Constants from '../../settings';
import move from '../../utilities/move';
import uuidv4 from '../../utilities/uuidv4';
import PlaylistItem from '../playlistItem/playlistItem';
import SyncSource from './syncSource';

export default class Playlist {
  static from(tracks: PlaylistItem[]) {
    const id = uuidv4();
    return new Playlist(id, tracks, id);
  }
  static activeFrom(tracks: PlaylistItem[]) {
    return new Playlist(Constants.ActivePlaylistName, tracks, Constants.ActivePlaylistId);
  }

  syncSources: SyncSource[] = [];
  /** Could be a set but that complicates toJson/fromJson */
  syncIgnore: {
    [index: string]: boolean;
  } = {};

  constructor(
    public name: string,
    public tracks: PlaylistItem[] = [],
    public id: string = '',
  ) {
    if (id === '') {
      this.id = uuidv4();
    }
  }
}

/** Handles upgrading older playlist files */
export function playlistUpgrader(pl: Playlist) {
  pl.syncSources = pl.syncSources || [];
  pl.syncIgnore = pl.syncIgnore || {};

  return pl;
}

/*
 * The helpers below take the playlist and the setter first and return the
 * operation, so a component can bind them once to the playlist it renders
 * and hand the bound operation down: `addItems(pl, updatePlaylist)` is a
 * function of the items alone.
 */

/** Appends one item to `playlist` and hands the result to `setPlaylist`. */
export const addItem =
  (playlist: Playlist, setPlaylist: (pl: Playlist) => void) => (playlistItem: PlaylistItem) =>
    setPlaylist({ ...playlist, tracks: [...playlist.tracks, playlistItem] });

/** Appends items to `playlist` and hands the result to `setPlaylist`. */
export const addItems =
  (playlist: Playlist, setPlaylist: (pl: Playlist) => void) => (playlistItems: PlaylistItem[]) =>
    setPlaylist({ ...playlist, tracks: [...playlist.tracks, ...playlistItems] });

interface Params {
  tasks: Promise<any>[];
  items: PlaylistItem[];
  addItems: (plItms: PlaylistItem[]) => void;
}
export function addMultipleItemsAsync({ tasks, items, addItems }: Params) {
  // Try to add all successful in browser agents supporting allSettled.
  // Read through a local alias: `'allSettled' in Promise` narrows the global
  // itself, leaving Promise as never in the else branch under TS 4.
  const promiseCtor: typeof Promise = Promise;

  if (typeof promiseCtor.allSettled === 'function') {
    return promiseCtor.allSettled(tasks).then(() => addItems(items));
  } else {
    return promiseCtor.all(tasks).then(() => addItems(items));
  }
}

export class PlaylistNotFoundError extends Error {}

/** Inserts an item at `index`. An index outside the playlist does nothing. */
export const insertPlaylistItem =
  (pl: Playlist, setPlaylist: (pl: Playlist) => void) =>
  (index: number, playlistItem: PlaylistItem) => {
    if (index < 0 || index > pl.tracks.length) {
      return;
    }

    const tracks = pl.tracks.slice();
    tracks.splice(index, 0, playlistItem);
    setPlaylist({ ...pl, tracks });
  };

export const insertTrack =
  (pl: Playlist, setPlaylist: (pl: Playlist) => void) => (index: number, track: Track) =>
    insertPlaylistItem(pl, setPlaylist)(index, PlaylistItem.from(track));

/** Currently does not care if we are deleting currentPlaylistItem */
export const removeItems =
  (playlist: Playlist, setPlaylist: (pl: Playlist) => void) =>
  (...itemsToRemove: PlaylistItem[]) => {
    setPlaylist({
      ...playlist,
      tracks: playlist.tracks.filter((x) => !itemsToRemove.includes(x)),
    });
  };
export const removeTrackNoPersist =
  (playlist: Playlist) =>
  (track: Track): Playlist => ({
    ...playlist,
    tracks: playlist.tracks.filter((x) => x.id !== track.id),
  });

export const moveItem =
  (playlist: Playlist, setPlaylist: (pl: Playlist) => void) => (from: number, to: number) =>
    setPlaylist({ ...playlist, tracks: move(from, to, playlist.tracks) });

export const moveItems = (playlist: Playlist, setPlaylist: (pl: Playlist) => void) =>
  function (itemsToMove: Set<PlaylistItem>, moveTarget: PlaylistItem | undefined, after: boolean) {
    if (moveTarget && itemsToMove.has(moveTarget)) {
      // Won't happen unless i code drunk
      throw new Error('Move target cannot be subject to a move.');
    }

    const itemsNotBeingMoved = playlist.tracks.filter((x) => !itemsToMove.has(x));

    if (moveTarget) {
      const targetIdx = after
        ? itemsNotBeingMoved.indexOf(moveTarget) + 1
        : itemsNotBeingMoved.indexOf(moveTarget);

      setPlaylist({
        ...playlist,
        tracks: [
          ...itemsNotBeingMoved.slice(0, targetIdx),
          ...itemsToMove,
          ...itemsNotBeingMoved.slice(targetIdx),
        ],
      });
    } else {
      setPlaylist({
        ...playlist,
        tracks: itemsNotBeingMoved.concat(...itemsToMove),
      });
    }
  };

/** The index to play before `curPlaylistItem`, or undefined when there is nothing to play. */
export function prevItemIndex(playlist: Playlist, curPlaylistItem: number): number | undefined {
  return playlist.tracks.length === 0
    ? undefined
    : curPlaylistItem === -1 || curPlaylistItem === 0
      ? 0
      : curPlaylistItem - 1;
}
/** The index to play after `curPlaylistItem`, or undefined when playback should stop. */
export function nextItemIndex(
  playlist: Playlist,
  curPlaylistItem: number,
  repeat: RepeatState,
): number | undefined {
  if (playlist.tracks.length === 0) {
    return undefined;
  }

  if (repeat === RepeatState.One) {
    return curPlaylistItem;
  }

  const lastIndex = playlist.tracks.length - 1;

  if (repeat === RepeatState.None && curPlaylistItem === lastIndex) {
    return undefined;
  }

  // RepeatState.All or None when playlist not finished
  return curPlaylistItem === -1 || curPlaylistItem === lastIndex ? 0 : curPlaylistItem + 1;
}
